import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import FlagSlideshow from '../components/FlagSlideshow';
import ThoranVisual from '../components/ThoranVisual';

const HomePage = () => {
  const [isVisible, setIsVisible] = useState({});

  // Flag categories with organized data from src/assets/flags
  const flagCategories = [
    {
      id: 'bharatiya-janata-party',
      name: 'Bharatiya Janata Party',
      displayName: 'BJP Flags',
      description: 'Official BJP party flags with authentic saffron, white and green colors',
      image: '/src/assets/flags/Bharatiya Janata Party/BJP flag.jpg',
      fallback: '/Main photos/BJP flag.jpg',
      color: '#FF9933',
      icon: '🟠',
      count: 4
    },
    {
      id: 'congress',
      name: 'Congress',
      displayName: 'Congress Party Flags',
      description: 'Indian National Congress flags with traditional blue, white designs',
      image: '/src/assets/flags/Congress/congress flags.jpeg',
      fallback: '/Main photos/congress flags.jpeg',
      color: '#0066CC',
      icon: '🔵',
      count: 4
    },
    {
      id: 'ganesh-chaturthi-flags',
      name: 'Ganesh chaturthi flags',
      displayName: 'Ganesh Festival Flags',
      description: 'Beautiful Lord Ganesh flags for Ganesh Chaturthi celebrations',
      image: '/src/assets/flags/Ganesh chaturthi flags/ganesh 1.jpeg',
      fallback: '/Main photos/ganesh 1.jpeg',
      color: '#FF6B35',
      icon: '🙏',
      count: 5
    },
    {
      id: 'hanuman-flags',
      name: 'Hanuman Flags',
      displayName: 'Hanuman Devotional Flags',
      description: 'Sacred Hanuman flags for temples and religious ceremonies',
      image: '/src/assets/flags/Hanuman Flags/IMG-20260125-WA0020.jpg',
      fallback: '/Main photos/Hanuman flag1.jpeg',
      color: '#FF4500',
      icon: '🕉️',
      count: 3
    },
    {
      id: 'telangana-rashtra-samithi',
      name: 'Telanagana Rastra samithi',
      displayName: 'TRS Political Flags',
      description: 'Telangana Rashtra Samithi flags for state political events',
      image: '/src/assets/flags/Telanagana Rastra samithi/trs flags 1.jpeg',
      fallback: '/Main photos/trs flags 1.jpeg',
      color: '#FF1493',
      icon: '🟣',
      count: 2
    },
    {
      id: 'telugu-desam-party',
      name: 'Telugu desam party',
      displayName: 'TDP Political Flags',
      description: 'Telugu Desam Party flags with vibrant yellow colors',
      image: '/src/assets/flags/Telugu desam party/tdp flags 1.jpeg',
      fallback: '/Main photos/tdp flags 1.jpeg',
      color: '#FFFF00',
      icon: '🟡',
      count: 3
    }
  ];

  // Features section data
  const features = [
    {
      icon: 'bi-shield-check',
      title: '100% Quality Guarantee',
      description: 'Premium materials and authentic colors for all our flags',
      color: 'success'
    },
    {
      icon: 'bi-truck',
      title: 'Fast Delivery',
      description: 'Quick delivery across India with tracking',
      color: 'warning'
    },
    {
      icon: 'bi-tools',
      title: 'Custom Designs',
      description: 'Personalized flags with your specifications',
      color: 'info'
    },
    {
      icon: 'bi-headset',
      title: '24/7 Support',
      description: 'Always here to help with your flag needs',
      color: 'primary'
    }
  ];

  // Scroll animation effect
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(prev => ({
            ...prev,
            [entry.target.dataset.section]: true
          }));
        }
      });
    }, observerOptions);

    // Observe all sections
    const sections = document.querySelectorAll('[data-section]');
    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  // Handle image error
  const handleImageError = (e, fallback) => {
    const img = e.target;
    if (img.src !== fallback) {
      img.src = fallback;
    }
  };

  return (
    <div className="homepage">
      
      {/* Full-Screen Flag Slideshow */}
      <section className="hero-section">
        <FlagSlideshow />
      </section>

      {/* Welcome Section */}
      <section 
        className={`welcome-section py-5 ${isVisible.welcome ? 'animate-fade-in' : ''}`}
        data-section="welcome"
      >
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <div className="welcome-content">
                <h2 className="display-4 fw-bold text-primary mb-4">
                  Welcome to SR Indian Flags
                </h2>
                <p className="lead text-muted mb-4">
                  India's premier flag manufacturing company, proudly serving the nation with 
                  high-quality flags for political parties, festivals, and patriotic celebrations 
                  since our inception.
                </p>
                <div className="stats-row row">
                  <div className="col-sm-4 mb-3">
                    <div className="stat-item text-center">
                      <h3 className="text-primary fw-bold">500+</h3>
                      <p className="text-muted mb-0">Happy Customers</p>
                    </div>
                  </div>
                  <div className="col-sm-4 mb-3">
                    <div className="stat-item text-center">
                      <h3 className="text-success fw-bold">1000+</h3>
                      <p className="text-muted mb-0">Flags Delivered</p>
                    </div>
                  </div>
                  <div className="col-sm-4 mb-3">
                    <div className="stat-item text-center">
                      <h3 className="text-warning fw-bold">6+</h3>
                      <p className="text-muted mb-0">Categories</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="welcome-image">
                <div className="image-collage">
                  <img 
                    src="/Main photos/Indian flag.jpg" 
                    alt="Indian Flag" 
                    className="img-fluid rounded shadow main-flag"
                  />
                  <div className="floating-elements">
                    <div className="floating-flag flag-1">
                      <img 
                        src="/Main photos/BJP flag.jpg" 
                        alt="BJP Flag" 
                        className="img-fluid rounded"
                      />
                    </div>
                    <div className="floating-flag flag-2">
                      <img 
                        src="/Main photos/ganesh 1.jpeg" 
                        alt="Ganesh Flag" 
                        className="img-fluid rounded"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Flag Categories Section */}
      <section 
        className={`categories-section py-5 bg-light ${isVisible.categories ? 'animate-slide-up' : ''}`}
        data-section="categories"
      >
        <div className="container">
          <div className="section-header text-center mb-5">
            <h2 className="display-4 fw-bold text-dark mb-3">
              🇮🇳 Our Flag Categories 🇮🇳
            </h2>
            <p className="lead text-muted">
              Discover our comprehensive collection of premium quality flags for every occasion
            </p>
          </div>

          <div className="row">
            {flagCategories.map((category, index) => (
              <div key={category.id} className="col-lg-4 col-md-6 mb-4">
                <div 
                  className="category-card h-100"
                  style={{ 
                    '--category-color': category.color,
                    animationDelay: `${index * 0.2}s`
                  }}
                >
                  <div className="card-image-container">
                    <img
                      src={category.image}
                      alt={category.displayName}
                      className="card-img-top"
                      onError={(e) => handleImageError(e, category.fallback)}
                    />
                    <div className="image-overlay">
                      <div className="overlay-content">
                        <span className="category-icon">{category.icon}</span>
                        <h5 className="text-white fw-bold">{category.displayName}</h5>
                      </div>
                    </div>
                    <div className="flag-count-badge">
                      {category.count} Designs
                    </div>
                  </div>
                  
                  <div className="card-body">
                    <h5 className="card-title fw-bold mb-3">
                      {category.displayName}
                    </h5>
                    <p className="card-text text-muted mb-3">
                      {category.description}
                    </p>
                    
                    <div className="category-actions">
                      <Link 
                        to={`/flags/${category.id}`}
                        className="btn btn-primary btn-category"
                        style={{ backgroundColor: category.color }}
                      >
                        <i className="bi bi-eye me-2"></i>
                        View Collection
                      </Link>
                      <a 
                        href="https://wa.me/919700388626"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-outline-success"
                      >
                        <i className="bi bi-whatsapp me-1"></i>
                        Order Now
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* View All Categories Button */}
          <div className="text-center mt-5">
            <Link to="/flags" className="btn btn-lg btn-outline-primary">
              <i className="bi bi-grid me-2"></i>
              View All Flag Categories
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section 
        className={`features-section py-5 ${isVisible.features ? 'animate-fade-in' : ''}`}
        data-section="features"
      >
        <div className="container">
          <div className="section-header text-center mb-5">
            <h2 className="display-4 fw-bold text-dark mb-3">
              Why Choose SR Indian Flags?
            </h2>
            <p className="lead text-muted">
              We're committed to delivering the finest quality flags with exceptional service
            </p>
          </div>

          <div className="row">
            {features.map((feature, index) => (
              <div key={index} className="col-lg-3 col-md-6 mb-4">
                <div 
                  className="feature-card text-center h-100"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className={`feature-icon text-${feature.color} mb-3`}>
                    <i className={`bi ${feature.icon} fs-1`}></i>
                  </div>
                  <h5 className="fw-bold mb-3">{feature.title}</h5>
                  <p className="text-muted">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section 
        className={`cta-section py-5 bg-primary text-white ${isVisible.cta ? 'animate-slide-up' : ''}`}
        data-section="cta"
      >
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-8">
              <h2 className="display-5 fw-bold mb-3">
                <i className="bi bi-telephone-fill me-3"></i>
                Ready to Order Your Flags?
              </h2>
              <p className="lead mb-4">
                Contact us today for bulk orders, custom designs, and competitive pricing. 
                We're here to serve all your flag needs with excellence and patriotic pride.
              </p>
              
              <div className="contact-info-row">
                <div className="contact-item me-4">
                  <i className="bi bi-telephone-fill me-2"></i>
                  <strong>Call:</strong> 9700388626
                </div>
                <div className="contact-item">
                  <i className="bi bi-whatsapp me-2"></i>
                  <strong>WhatsApp:</strong> Available 24/7
                </div>
              </div>
            </div>
            <div className="col-lg-4 text-lg-end">
              <div className="cta-buttons">
                <a 
                  href="tel:9700388626" 
                  className="btn btn-light btn-lg mb-3 w-100"
                >
                  <i className="bi bi-telephone-fill me-2"></i>
                  Call Now: 9700388626
                </a>
                <a 
                  href="https://wa.me/919700388626"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-success btn-lg w-100"
                >
                  <i className="bi bi-whatsapp me-2"></i>
                  WhatsApp Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Specials – Buntings & Thorans */}
      <section className="py-5" style={{ background: 'linear-gradient(135deg, #fff8f0, #fff3e0)' }}>
        <div className="container">
          <div className="section-header text-center mb-4">
            <h2 className="fw-bold" style={{ color: '#ff6600' }}>⭐ Our Specials</h2>
            <p className="text-muted">Premium Buntings & Thorans – All Colours, Bulk Orders Available</p>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '16px'
          }}>
            {[
              { id: 32, name: 'Kesari Saffron', colors: ['#FF9933','#FF9933','#FF9933','#FF9933','#FF9933'], bg: '#FFF3E0', accent: '#FF9933', desc: 'Religious events, rallies & pandals' },
              { id: 33, name: 'Tricolor (National)', colors: ['#FF9933','#FFFFFF','#138808','#FF9933','#FFFFFF','#138808'], bg: '#F0F8FF', accent: '#138808', desc: 'Independence Day & national events' },
              { id: 34, name: 'BJP Orange', colors: ['#FF6600','#FF8C00','#FF6600','#FF8C00','#FF6600'], bg: '#FFF5EE', accent: '#FF6600', desc: 'Political rallies & campaigns' },
              { id: 35, name: 'Jai Bhim Blue & Yellow', colors: ['#1565C0','#FDD835','#1565C0','#FDD835','#1565C0','#FDD835'], bg: '#E3F2FD', accent: '#1565C0', desc: 'Ambedkar Jayanti & social events' },
              { id: 36, name: 'Karnataka Red & Yellow', colors: ['#CC0000','#FFD700','#CC0000','#FFD700','#CC0000'], bg: '#FFF8E1', accent: '#CC0000', desc: 'Rajyotsava & state celebrations' },
              { id: 37, name: 'Festive Green', colors: ['#28A745','#20C997','#28A745','#20C997','#28A745'], bg: '#F0FFF4', accent: '#28A745', desc: 'Eid, dargah & festive occasions' },
              { id: 40, name: 'Multicolor Rainbow', colors: ['#FF4444','#FF9933','#FDD835','#28A745','#1565C0','#9B59B6'], bg: '#F8F0FF', accent: '#9B59B6', desc: 'All festivals & celebrations' },
              { id: 41, name: 'Red Thoran', colors: ['#CC0000','#E53935','#CC0000','#E53935','#CC0000'], bg: '#FFF5F5', accent: '#CC0000', desc: 'Congress, trade union & events' },
              { id: 42, name: 'Large Scale Event Thoran', photo: '/Main photos/thoran-event.jpg', colors: null, bg: '#FFF3E0', accent: '#FF6600', desc: 'City events, roundabouts & big rallies' },
            ].map((item) => (
              <div key={item.id} style={{
                background: '#fff', borderRadius: '14px', overflow: 'hidden',
                boxShadow: '0 2px 16px rgba(0,0,0,0.08)',
                border: `2px solid ${item.accent}33`
              }}>
                <div style={{ position: 'relative' }}>
                  {item.photo ? (
                    <img src={item.photo} alt={item.name}
                      style={{ width: '100%', height: '150px', objectFit: 'cover', display: 'block' }} />
                  ) : (
                    <ThoranVisual thoranColors={item.colors} bgColor={item.bg} height={150} />
                  )}
                  <div style={{
                    position: 'absolute', top: '8px', left: '8px',
                    background: item.accent, color: item.accent === '#FDD835' || item.accent === '#FFD700' ? '#333' : '#fff',
                    fontSize: '10px', fontWeight: 700, padding: '3px 10px', borderRadius: '10px'
                  }}>OUR SPECIAL</div>
                </div>
                <div style={{ padding: '12px' }}>
                  <p style={{ fontWeight: 700, color: '#222', marginBottom: '3px', fontSize: '14px' }}>{item.name}</p>
                  <p style={{ color: '#888', fontSize: '11px', marginBottom: '10px' }}>{item.desc}</p>
                  <a
                    href={`https://wa.me/919700388626?text=Hi, I need a quote for ${encodeURIComponent(item.name)} thoran. Please share details.`}
                    target="_blank" rel="noopener noreferrer"
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                      background: '#25d366', color: '#fff', borderRadius: '8px',
                      padding: '8px', fontWeight: 700, fontSize: '13px', textDecoration: 'none'
                    }}
                  >
                    <i className="bi bi-whatsapp"></i> Get Quote
                  </a>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-4">
            <Link to="/flags/buntings" style={{
              display: 'inline-block', padding: '12px 32px',
              background: '#ff6600', color: '#fff', borderRadius: '10px',
              fontWeight: 700, textDecoration: 'none', fontSize: '15px'
            }}>
              View All Buntings & Thorans →
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section 
        className={`testimonials-section py-5 bg-light ${isVisible.testimonials ? 'animate-fade-in' : ''}`}
        data-section="testimonials"
      >
        <div className="container">
          <div className="section-header text-center mb-5">
            <h2 className="display-4 fw-bold text-dark mb-3">
              What Our Customers Say
            </h2>
            <p className="lead text-muted">
              Trusted by political parties, organizations, and individuals across India
            </p>
          </div>

          <div className="row">
            <div className="col-lg-4 mb-4">
              <div className="testimonial-card">
                <p className="text-muted mb-3">
                  "Excellent quality BJP flags for our party rally. The colors were vibrant
                  and the material was durable. Highly recommended!"
                </p>
                <div className="customer-info">
                  <strong>Ramesh Kumar</strong>
                  <small className="text-muted d-block">Party Organizer</small>
                </div>
              </div>
            </div>
            
            <div className="col-lg-4 mb-4">
              <div className="testimonial-card">
                <p className="text-muted mb-3">
                  "Perfect Ganesh flags for our temple festival. Beautiful designs and
                  quick delivery. Will definitely order again next year!"
                </p>
                <div className="customer-info">
                  <strong>Priya Sharma</strong>
                  <small className="text-muted d-block">Temple Committee</small>
                </div>
              </div>
            </div>
            
            <div className="col-lg-4 mb-4">
              <div className="testimonial-card">
                <p className="text-muted mb-3">
                  "Outstanding service and quality! The Indian flags we ordered for
                  Independence Day were absolutely perfect. Thank you!"
                </p>
                <div className="customer-info">
                  <strong>Suresh Patel</strong>
                  <small className="text-muted d-block">School Principal</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default HomePage;